import { Box, BoxProps, useMediaQuery, useTheme } from '@mui/material';
import { ReactNode, useMemo } from 'react';

import { Dialog } from '@/components/modals/Dialog';
import { getNavyGradientBackground } from '@/styles/styleUtils';
import { LuminaryDarkThemeProvider } from '@/styles/themes/LuminaryDarkThemeProvider';
import { COLORS } from '@/styles/tokens/colors';
import { zIndexes } from '@/styles/zIndexes';

export interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  aboveTitleContent?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  sidebarFooterContent?: ReactNode;
  mainContent: ReactNode;
  sidebarContent?: ReactNode;
  contentProps?: BoxProps;
  sidebarProps?: BoxProps;
}

export function OnboardingModal({
  isOpen,
  onClose,
  aboveTitleContent,
  title,
  description,
  sidebarFooterContent,
  mainContent,
  sidebarContent,
  contentProps,
  sidebarProps,
}: OnboardingModalProps) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const responsiveSidebarWidth = useMemo(() => {
    if (isSmallScreen) return '275px'; // Minimum width
    if (isMediumScreen) return '350px'; // Medium reduction
    return '425px'; // Default for large screens
  }, [isMediumScreen, isSmallScreen]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      sx={{
        zIndex: zIndexes.MODAL,
      }}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '90%',
          height: '90%',
          maxWidth: '1440px',
          maxHeight: '900px',
          m: 'auto',
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <Box display="flex" height="100%">
        <LuminaryDarkThemeProvider>
          <Box
            width={responsiveSidebarWidth}
            sx={{
              backgroundImage: getNavyGradientBackground(),
            }}
            display="flex"
            flexShrink={0}
            flexDirection="column"
            height="100%"
            p={2}
            pt={6}
            {...sidebarProps}
          >
            <Box mx={2}>
              {aboveTitleContent}
              {/* this is a little odd, but we actually want *more* spacing above if there's no aboveTitleContent
              to make the title more or less in the same position */}
              <Box mt={aboveTitleContent ? 2 : 0}>{title}</Box>
              {description && <Box mt={2}>{description}</Box>}
            </Box>
            {sidebarContent && <Box mt={2}>{sidebarContent}</Box>}
            <Box flexGrow={1} />
            {sidebarFooterContent && <Box>{sidebarFooterContent}</Box>}
          </Box>
        </LuminaryDarkThemeProvider>
        <Box
          flexGrow={1}
          bgcolor={COLORS.PRIMITIVES.WHITE}
          height="100%"
          overflow="auto"
          p={2}
          {...contentProps}
        >
          {mainContent}
        </Box>
      </Box>
    </Dialog>
  );
}
